import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, Edit, GripVertical } from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/api';

const IMG_BASE = process.env.REACT_APP_IMG_URL || 'http://localhost:5000';

// ── LIST ─────────────────────────────────────────────────
export function AdminSliderList() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/slider').then(r => setSlides(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Fshi slidën "${title || id}"?`)) return;
    try {
      await api.delete(`/admin/slider/${id}`);
      toast.success('Slida u fshi.');
      load();
    } catch { toast.error('Gabim.'); }
  };

  const toggleActive = async (slide) => {
    try {
      const newActive = slide.is_active ? 0 : 1;
      await api.put(`/admin/slider/${slide.id}`, {
        title:       slide.title       || '',
        subtitle:    slide.subtitle    || '',
        button_text: slide.button_text || '',
        button_url:  slide.button_url  || '',
        sort_order:  slide.sort_order  || 0,
        is_active:   newActive,
      });
      toast.success(newActive ? 'Slida u aktivizua.' : 'Slida u çaktivizua.');
      load();
    } catch { toast.error('Gabim.'); }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">Slideri (Faqja Kryesore)</h1>
          <p className="admin-page-sub">{slides.length} slide gjithsej</p>
        </div>
        <Link to="/admin/slider/i-ri" className="btn btn-primary"><Plus size={15}/> Slide i ri</Link>
      </div>

      {loading ? <div className="spinner"/> : (
        <div className="slider-admin-grid">
          {slides.map(s => (
            <div key={s.id} className="slider-admin-card card">
              <div className="slider-admin-img">
                <img src={`${IMG_BASE}${s.image}`} alt={s.title || 'Slide'}/>
                <span className={`slide-status-badge ${s.is_active ? 'active' : 'inactive'}`}>
                  {s.is_active ? 'Aktiv' : 'Joaktiv'}
                </span>
              </div>
              <div className="slider-admin-body">
                <div className="slider-admin-order">
                  <GripVertical size={14} color="var(--gray-400)"/>
                  <span style={{fontSize:'0.75rem',color:'var(--gray-400)'}}>#{s.sort_order}</span>
                </div>
                <h3 className="slider-admin-title">{s.title || <em style={{color:'var(--gray-400)'}}>Pa titull</em>}</h3>
                {s.subtitle && <p className="slider-admin-sub">{s.subtitle}</p>}
                {s.button_text && (
                  <p style={{fontSize:'0.75rem',color:'var(--blue-700)',marginTop:4}}>
                    Butoni: "{s.button_text}" → {s.button_url}
                  </p>
                )}
              </div>
              <div className="slider-admin-actions">
                <button
                  className={`btn btn-sm ${s.is_active ? 'btn-secondary' : 'btn-primary'}`}
                  onClick={() => toggleActive(s)}
                >{s.is_active ? 'Çaktivizo' : 'Aktivizo'}</button>
                <Link to={`/admin/slider/${s.id}/ndrysho`} className="btn btn-secondary btn-sm"><Edit size={13}/></Link>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s.id, s.title)}><Trash2 size={13}/></button>
              </div>
            </div>
          ))}
          {slides.length === 0 && (
            <div style={{padding:'3rem',textAlign:'center',color:'var(--gray-400)',gridColumn:'1/-1'}}>
              Nuk ka slide. Shtoni slidën e parë!
            </div>
          )}
        </div>
      )}

      <style>{`
        .slider-admin-grid { display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:1rem; }
        .slider-admin-card { overflow:hidden;display:flex;flex-direction:column; }
        .slider-admin-img { height:180px;overflow:hidden;position:relative; }
        .slider-admin-img img { width:100%;height:100%;object-fit:cover; }
        .slide-status-badge { position:absolute;top:8px;right:8px;padding:3px 10px;border-radius:20px;font-size:0.7rem;font-weight:600; }
        .slide-status-badge.active { background:#D1FAE5;color:#065F46; }
        .slide-status-badge.inactive { background:#F3F4F6;color:#6B7280; }
        .slider-admin-body { padding:1rem;flex:1; }
        .slider-admin-order { display:flex;align-items:center;gap:4px;margin-bottom:6px; }
        .slider-admin-title { font-size:0.9rem;font-weight:600;color:var(--gray-900);margin-bottom:4px; }
        .slider-admin-sub { font-size:0.78rem;color:var(--gray-500);line-height:1.5; }
        .slider-admin-actions { padding:0.75rem 1rem;border-top:1px solid #eae9e4;display:flex;gap:6px; }
      `}</style>
    </AdminLayout>
  );
}

// ── FORM ─────────────────────────────────────────────────
export function AdminSliderForm() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = Boolean(id);

  const [form, setForm] = useState({
    title: '', subtitle: '', button_text: '', button_url: '', sort_order: 0, is_active: true
  });
  const [imgFile, setImgFile]     = useState(null);
  const [imgPreview, setImgPreview] = useState('');
  const [loading, setLoading]     = useState(false);
  const [fetching, setFetching]   = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    api.get('/admin/slider').then(r => {
      const s = r.data.find(x => String(x.id) === id);
      if (s) {
        setForm({
          title: s.title||'', subtitle: s.subtitle||'',
          button_text: s.button_text||'', button_url: s.button_url||'',
          sort_order: s.sort_order||0, is_active: Boolean(s.is_active)
        });
        if (s.image) setImgPreview(`${IMG_BASE}${s.image}`);
      }
    }).finally(() => setFetching(false));
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !imgFile) return toast.error('Zgjidhni foton e slideës.');
    setLoading(true);
    const fd = new FormData();
    fd.append('title',       form.title       || '');
    fd.append('subtitle',    form.subtitle     || '');
    fd.append('button_text', form.button_text  || '');
    fd.append('button_url',  form.button_url   || '');
    fd.append('sort_order',  form.sort_order   || 0);
    fd.append('is_active',   form.is_active ? '1' : '0');
    if (imgFile) fd.append('image', imgFile);
    try {
      if (isEdit) {
        await api.put(`/admin/slider/${id}`, fd, { headers: {'Content-Type':'multipart/form-data'} });
        toast.success('Slida u përditësua.');
      } else {
        await api.post('/admin/slider', fd, { headers: {'Content-Type':'multipart/form-data'} });
        toast.success('Slida u shtua.');
      }
      navigate('/admin/slider');
    } catch(err) {
      toast.error(err.response?.data?.message || 'Gabim.');
    } finally { setLoading(false); }
  };

  if (fetching) return <AdminLayout><div className="spinner"/></AdminLayout>;

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="page-title">{isEdit ? 'Ndrysho Slidën' : 'Slide i Ri'}</h1>
        <Link to="/admin/slider" className="btn btn-secondary">← Kthehu</Link>
      </div>

      <form onSubmit={handleSubmit} style={{display:'grid',gridTemplateColumns:'1fr 300px',gap:'1.25rem',alignItems:'start'}}>
        <div className="card" style={{padding:'1.5rem'}}>
          <div className="form-group">
            <label className="form-label">Titulli (opsional)</label>
            <input className="form-input" placeholder="Titulli mbi foto..."
              value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/>
          </div>
          <div className="form-group">
            <label className="form-label">Nëntitulli / Përshkrimi</label>
            <textarea className="form-input" rows={3} placeholder="Tekst shtesë nën titull..."
              value={form.subtitle} onChange={e=>setForm(p=>({...p,subtitle:e.target.value}))}/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem'}}>
            <div className="form-group">
              <label className="form-label">Teksti i butonit</label>
              <input className="form-input" placeholder="p.sh. Mëso më shumë"
                value={form.button_text} onChange={e=>setForm(p=>({...p,button_text:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">URL e butonit</label>
              <input className="form-input" placeholder="/departamentet"
                value={form.button_url} onChange={e=>setForm(p=>({...p,button_url:e.target.value}))}/>
            </div>
          </div>
        </div>

        <div>
          <div className="card" style={{padding:'1.25rem',marginBottom:'1rem'}}>
            <h3 className="form-side-title">Foto e slideës *</h3>
            {imgPreview && (
              <img src={imgPreview} alt="" style={{width:'100%',borderRadius:8,marginBottom:10,objectFit:'cover',height:140}}/>
            )}
            <input type="file" accept="image/*" className="form-input"
              onChange={e=>{const f=e.target.files[0];setImgFile(f);if(f)setImgPreview(URL.createObjectURL(f));}}
              required={!isEdit}/>
            <p style={{fontSize:'0.7rem',color:'var(--gray-400)',marginTop:4}}>Rekomandohet: 1400×600px, JPG/PNG</p>
          </div>

          <div className="card" style={{padding:'1.25rem',marginBottom:'1rem'}}>
            <h3 className="form-side-title">Renditja</h3>
            <input className="form-input" type="number" min="0" value={form.sort_order}
              onChange={e=>setForm(p=>({...p,sort_order:Number(e.target.value)}))}/>
          </div>

          <div className="card" style={{padding:'1.25rem',marginBottom:'1rem'}}>
            <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:'0.875rem',fontWeight:500}}>
              <input type="checkbox" checked={form.is_active}
                onChange={e=>setForm(p=>({...p,is_active:e.target.checked}))}/>
              Aktiv (shfaqet në faqe)
            </label>
          </div>

          <button type="submit" className="btn btn-primary" style={{width:'100%',justifyContent:'center'}} disabled={loading}>
            {loading ? 'Duke ruajtur...' : isEdit ? 'Ruaj ndryshimet' : 'Shto slidën'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}
